'use client';
import { gql } from '@apollo/client';
import type { MonitorSourceVerification, Sentiment } from '@mnd-frontend/graphql-schema';
import { Box, Button } from '@mnd-frontend/ui/publish/components';
import { GREY_400, SPACING_1, SPACING_2, SPACING_4 } from '@mnd-frontend/ui/shared/tokens';
import { Fragment } from 'react';
import styled from 'styled-components';
import { useHitsByIdsQuery, useSentimentHitsQuery } from '../../../../../generated/hooks';
import { MentionCard } from '../../../../monitor/MentionCard';
import { useReportStaticData, useReportTranslation } from '../../../hooks';
import { SkeletonHit, SkeletonWrapper } from '../Skeleton';

gql`
  query HitsByIds($reportId: ID!, $from: DateTime!, $to: DateTime!, $searchProfileIds: [ID!], $language: UserLocale) {
    report2(id: $reportId) {
      id
      ... on BrandReport {
        hits: hits2(
          timeRange: { from: $from, to: $to }
          orderBy: REACH
          searchProfileIds: $searchProfileIds
        ) {
          ...HitData
        }
      }
    }
  }

  query SentimentHits(
    $reportId: ID!
    $from: DateTime!
    $to: DateTime!
    $sentiment: Sentiment
    $searchProfileIds: [ID!]
  ) {
    report2(id: $reportId) {
      id
      ... on BrandReport {
        sentimentAnalysisHits(
          timeRange: { from: $from, to: $to }
          sentiment: $sentiment
          searchProfileIds: $searchProfileIds
        ) {
          id
          sentiment
          sentimentExplanation
          sourceName
          publishedAt
          title
          hitLine
          link
          thumbnailUrl
          reach
          sourceVerification
          audioUrl
          audioHitlines {
            id
            startPos
            endPos
            hitline
          }
        }
      }
    }
  }
`;

export interface HitsModalDetails {
  startAt: string;
  endAt: string;
  label: string;
  filter?: Sentiment;
}

export const HitsModal = ({
  onLinkClick,
  onClose,
  details,
  searchProfileIds,
}: {
  onLinkClick?: () => void;
  onClose: () => void;
  details: HitsModalDetails;
  searchProfileIds?: string[] | null;
}) => {
  const { t, lng } = useReportTranslation();
  const { id: reportId } = useReportStaticData();
  const { startAt, endAt } = details;
  const { data, loading } = useHitsByIdsQuery({
    variables: {
      reportId,
      from: startAt,
      to: endAt,
      searchProfileIds,
      language: lng,
    },
  });
  const hits = data?.report2?.__typename === 'BrandReport' ? data?.report2.hits : [];

  return (
    <Inner
      header={t('reports_sentiment_articles_in_time_frame', {
        timeLabel: details.label,
      })}
      hits={hits}
      loading={loading}
      onClose={onClose}
      onLinkClick={onLinkClick}
    />
  );
};

export const HitsModalSentiment = ({
  onLinkClick,
  onClose,
  details,
  searchProfileIds,
}: {
  onLinkClick?: () => void;
  onClose: () => void;
  details: HitsModalDetails;
  searchProfileIds?: string[] | null;
}) => {
  const { t } = useReportTranslation();
  const { id: reportId } = useReportStaticData();
  const { startAt, endAt } = details;
  const { data, loading } = useSentimentHitsQuery({
    variables: {
      reportId,
      from: startAt,
      to: endAt,
      sentiment: details.filter,
      searchProfileIds,
    },
  });
  const hits =
    data?.report2?.__typename === 'BrandReport' ? data?.report2.sentimentAnalysisHits : [];

  return (
    <Inner
      header={t('reports_sentiment_articles_in_time_frame', {
        timeLabel: details.label,
      })}
      hits={hits}
      loading={loading}
      onClose={onClose}
      onLinkClick={onLinkClick}
      showSentiment={true}
    />
  );
};

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_4};
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${GREY_400};
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};
  &:not(:last-child)::after {
    content: '';
    display: block;
    width: 100%;
    border-bottom: 1px solid ${GREY_400};
    margin-top: ${SPACING_2};
  }
`;

const Inner = ({
  loading,
  onClose,
  hits,
  onLinkClick,
  header,
  showSentiment,
}: {
  loading: boolean;
  header: string;
  onClose: () => void;
  hits: {
    id: string;
    sourceName?: string | null;
    publishedAt: string;
    title?: string | null;
    thumbnailUrl?: string | null;
    link?: string | null;
    hitLine?: string | null;
    reach: number;
    sentiment?: Sentiment | null;
    sourceVerification: MonitorSourceVerification;
    audioUrl?: string | null;
    audioHitlines?: { startPos: number; endPos: number; hitline: string }[];
  }[];
  onLinkClick?: () => void;
  showSentiment?: boolean;
}) => {
  const { t } = useReportTranslation();

  return (
    <Box
      openedAsModal
      size="lg"
      onClose={onClose}
      loading={loading}
      description={header}
      footerContent={
        <>
          {onLinkClick ? (
            <Button onClick={onLinkClick} configuration="text" type="button">
              {t('preview_media_feed-see-more')}
            </Button>
          ) : null}
          <Button onClick={onClose} type="button">
            {t('common_close')}
          </Button>
        </>
      }
    >
      <List>
        {loading &&
          Array.from({ length: 2 }).map((_, i) => (
            <Item key={i}>
              <SkeletonWrapper>
                <SkeletonHit />
              </SkeletonWrapper>
            </Item>
          ))}
        {!loading && hits.length === 0 && <Item>{t('reports_no_mentions_found')}</Item>}
        {!loading &&
          hits.map((hit, index) => (
            <Fragment key={hit.id}>
              <MentionCard
                hit={{
                  ...hit,
                  audioHitlines: hit.audioHitlines?.map((audioHitline, index) => ({
                    id: `${hit.id}-audio-${index}`,
                    startPos: audioHitline.startPos,
                    endPos: audioHitline.endPos,
                    hitline: audioHitline.hitline,
                  })),
                }}
                monitorSentiment={showSentiment}
                checkSourceVerification
                noPadding
              />
              {index < hits.length - 1 && <Divider />}
            </Fragment>
          ))}
      </List>
    </Box>
  );
};
